import { ColumnMenuButton, HeaderButtonsContainer, OperationListHeaderStyle } from './OperationListView.styles';
import { useStowageStore } from 'src/context/StowageContext';
import { SortStowageColumnEnum } from 'src/enums/stowage/sortStowageColumnEnum';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect } from 'react';
import OperationColumnComponent from './OperationColumnComponent';
import { getSavedColumnWidths } from './utils/columnWidthUtils';
import { getVisibleColumns } from './utils/columnConfig';
import { applyColumnOrder, reorderColumns, saveColumnOrder } from './utils/columnOrderUtils';

const OperationListHeaderComponent: React.FC = () => {
    const {
        isColumnsPanelOpen,
        setIsColumnsPanelOpen,
        operationListColumns,
        setOperationListSortColumn,
        operationListSortColumn,
        setIsOperationListSortDesc,
        isOperationListSortDesc,
        setOperationListColumnWidths,
        setMouseClickPosition,
        orderedColumns,
        setOrderedColumns,
    } = useStowageStore();

    useEffect(() => {
        const isOperationListSortDesc_ls = localStorage.getItem('isOperationListSortDesc');
        if (isOperationListSortDesc_ls) {
            setIsOperationListSortDesc(JSON.parse(isOperationListSortDesc_ls));
        }

        const operationListSortColumn_ls = localStorage.getItem('operationListSortColumn');
        if (operationListSortColumn_ls) {
            setOperationListSortColumn(JSON.parse(operationListSortColumn_ls));
        }

        // Load column widths using the utility function
        const savedWidths = getSavedColumnWidths();
        setOperationListColumnWidths(savedWidths);

        // Force columns to apply widths
        setTimeout(() => {
            const columns = document.querySelectorAll('[data-column-name]');
            columns.forEach((column: Element) => {
                const columnName = column.getAttribute('data-column-name');
                if (columnName && savedWidths[columnName]) {
                    (column as HTMLElement).style.width = savedWidths[columnName];
                }
            });
        }, 100);
    }, []);

    useEffect(() => {
        const visibleCols = getVisibleColumns(operationListColumns || null);
        const orderedVisibleCols = applyColumnOrder(visibleCols);
        setOrderedColumns(orderedVisibleCols);
    }, [operationListColumns, setOrderedColumns]);

    const onOpenColumnPanel = (e) => {
        if (!isColumnsPanelOpen) {
            setMouseClickPosition({
                top: e.clientY,
                left: e.clientX,
            });
        } else {
            setMouseClickPosition(null);
        }
        setIsColumnsPanelOpen(!isColumnsPanelOpen);
    };

    // Handle right-click context menu event
    const handleContextMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault(); // Prevent the default context menu
        onOpenColumnPanel(e);
    };

    const onChangeSort = (clickedColumn: SortStowageColumnEnum) => {
        if (clickedColumn === operationListSortColumn) {
            // Same column clicked - cycle through states
            if (isOperationListSortDesc) {
                // Currently descending → go to NoSort
                setOperationListSortColumn(SortStowageColumnEnum.NoSort);
                localStorage.setItem('operationListSortColumn', JSON.stringify(SortStowageColumnEnum.NoSort));
            } else {
                // Currently ascending → go to descending
                setIsOperationListSortDesc(true);
                localStorage.setItem('isOperationListSortDesc', JSON.stringify(true));
            }
        } else {
            // Different column clicked → start with ascending
            setOperationListSortColumn(clickedColumn);
            setIsOperationListSortDesc(false);
            localStorage.setItem('operationListSortColumn', JSON.stringify(clickedColumn));
            localStorage.setItem('isOperationListSortDesc', JSON.stringify(false));
        }
    };

    const getSortArrow = () => {
        return <FontAwesomeIcon className="sort-icon" title="Click to change sorting" icon={isOperationListSortDesc ? 'arrow-down' : 'arrow-up'} />;
    };

    const handleDragStart = (index: number) => () => {
        // Store the dragged index in a data attribute for use during drag operations
        // This avoids needing local component state
        const draggableElements = document.querySelectorAll('[data-column-index]');
        draggableElements.forEach((el) => {
            el.setAttribute('data-is-dragging', 'false');
        });

        const draggingElement = document.querySelector(`[data-column-index="${index}"]`);
        if (draggingElement) {
            draggingElement.setAttribute('data-is-dragging', 'true');
        }
    };

    const handleDrop = (targetIndex: number) => (e: React.DragEvent<HTMLSpanElement>) => {
        e.preventDefault();

        // Find the dragging element
        const draggingElement = document.querySelector('[data-is-dragging="true"]');
        if (!draggingElement) return;

        const draggedIndex = parseInt(draggingElement.getAttribute('data-column-index') || '-1');
        if (draggedIndex === -1 || draggedIndex === targetIndex) return;

        // Reorder the columns
        const newOrderedColumns = reorderColumns(orderedColumns, draggedIndex, targetIndex);
        setOrderedColumns(newOrderedColumns);

        // Save the new order
        const columnIds = newOrderedColumns.map((column) => column.id);
        saveColumnOrder(columnIds);

        // Reset drag state
        draggingElement.setAttribute('data-is-dragging', 'false');
    };

    const handleDragEnd = () => {
        // Clean up any drag markers
        const draggableElements = document.querySelectorAll('[data-column-index]');
        draggableElements.forEach((el) => {
            el.setAttribute('data-is-dragging', 'false');
        });
    };

    // Render the first column with the buttons - this column is not draggable
    const renderButtonsColumn = () => (
        <OperationColumnComponent columnName="statusIcon" className="col-menu status-icon" showResizer={true} isDraggable={false}>
            <HeaderButtonsContainer>
                <ColumnMenuButton onClick={(e) => onOpenColumnPanel(e)} title="Choose columns" isActive={isColumnsPanelOpen}>
                    ...
                </ColumnMenuButton>
            </HeaderButtonsContainer>
        </OperationColumnComponent>
    );

    // Render other columns based on visibility settings and order
    const renderColumns = () => {
        return orderedColumns
            .map((column, index) => {
                if (column.id === 'statusIcon') return null; // Already rendered separately

                const isSortableColumn = column.sortEnum !== undefined;
                const className = column.cssClass || '';

                return (
                    <OperationColumnComponent
                        key={column.id}
                        columnName={column.id}
                        className={`${className}`}
                        onClick={isSortableColumn ? () => onChangeSort(column.sortEnum!) : undefined}
                        title={column.label}
                        showResizer={true}
                        onDragStart={handleDragStart(index)}
                        onDrop={handleDrop(index)}
                        onDragEnd={handleDragEnd}
                        data-column-index={index}
                        data-is-dragging="false"
                        isActive={
                            isSortableColumn &&
                            operationListSortColumn === column.sortEnum &&
                            operationListSortColumn !== SortStowageColumnEnum.NoSort
                        }
                    >
                        {isSortableColumn &&
                            operationListSortColumn === column.sortEnum &&
                            operationListSortColumn !== SortStowageColumnEnum.NoSort &&
                            getSortArrow()}
                        {column.label}
                    </OperationColumnComponent>
                );
            })
            .filter(Boolean);
    };

    return (
        <OperationListHeaderStyle className="operation-list-header" onContextMenu={handleContextMenu}>
            {renderButtonsColumn()}
            {renderColumns()}
        </OperationListHeaderStyle>
    );
};

export default OperationListHeaderComponent;
